const RATE_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})(?:_|$)/;

function isStoredRateValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return value !== undefined && value !== null && value !== false;
}

/**
 * The existing rate download API returns a flat object whose keys begin with
 * YYYY-MM-DD. Convert that response into a stable, de-duplicated list for the
 * calendar without changing the API contract.
 */
export function extractAvailableRateDates(ratePayload) {
  if (!ratePayload || typeof ratePayload !== "object" || Array.isArray(ratePayload)) {
    return [];
  }

  const dates = new Set();

  Object.entries(ratePayload).forEach(([key, value]) => {
    const match = String(key).match(RATE_DATE_PREFIX);

    if (match && isStoredRateValue(value)) {
      dates.add(match[1]);
    }
  });

  return Array.from(dates).sort();
}

function isIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function monthRange(monthKey) {
  const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;

  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    start: `${match[1]}-${match[2]}-01`,
    end: `${match[1]}-${match[2]}-${String(lastDay).padStart(2, "0")}`,
  };
}

function nextDate(dateKey) {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/**
 * Cover the selected range plus each calendar's visible month. Overlapping
 * intervals are merged, while a user browsing a distant month causes only a
 * separate one-month request instead of one unbounded request across the gap.
 */
export function buildRateAvailabilityRanges(selectedStart, selectedEnd, visibleMonths = []) {
  const ranges = [];

  if (isIsoDate(selectedStart) && isIsoDate(selectedEnd)) {
    ranges.push({
      start: selectedStart <= selectedEnd ? selectedStart : selectedEnd,
      end: selectedStart <= selectedEnd ? selectedEnd : selectedStart,
    });
  }

  visibleMonths.forEach((monthKey) => {
    const range = monthRange(monthKey);
    if (range) ranges.push(range);
  });

  return ranges
    .sort((left, right) => left.start.localeCompare(right.start))
    .reduce((merged, range) => {
      const previous = merged[merged.length - 1];

      if (!previous || range.start > nextDate(previous.end)) {
        merged.push({ ...range });
      } else if (range.end > previous.end) {
        previous.end = range.end;
      }

      return merged;
    }, []);
}

export function mergeRatePayloads(payloads) {
  return (Array.isArray(payloads) ? payloads : []).reduce((merged, payload) => {
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      Object.assign(merged, payload);
    }
    return merged;
  }, {});
}

export function filterRatePayloadByDateRange(ratePayload, start, end) {
  if (!ratePayload || typeof ratePayload !== "object" || !isIsoDate(start) || !isIsoDate(end)) {
    return {};
  }

  const first = start <= end ? start : end;
  const last = start <= end ? end : start;

  return Object.fromEntries(Object.entries(ratePayload).filter(([key]) => {
    const match = String(key).match(RATE_DATE_PREFIX);
    return match && match[1] >= first && match[1] <= last;
  }));
}
